// The oud style's band (docs/AUDIO_DESIGN.md §12): a ney (the end-blown reed flute: breathy, airy, sliding) whose melody
// is bent into the track's maqam and ornamented with turns and trills in it, and a darbuka (the goblet drum: a deep doum,
// a sharp tek, a softer ka and its rolls) with a riq's jingles. Persistent voices gated with setTargetAtTime, like
// instruments.js: nothing is allocated per note or hit.
import { rng, clamp } from '../../engine/core.js';
import { mtof, hold, noise } from '../dsp.js';
import { LEVELS, gain, filter, osc, panner, persistent } from '../instruments.js';
import { seedOf } from './oud-voice.js';

// Levels (tuning knobs for the mix balance), against the jazzhop lead and kit they replace.
export const BAND = { lead: 1.8, kit: 0.92 };
const mod12 = n => ((n % 12) + 12) % 12;

/* ---------------- ney / kawala ---------------- */
//   triangle share · 2nd harmonic · breath held · breath onset · breath BP (× f) · attack τ · LP · vibrato Hz, ±¢ · scoop odds
const NEY = { tri: 0.1, h2: 0.05, breath: 2.6, onset: 5, bp: 1.9, atk: 0.065, lp: 3400, vib: [4.6, 13], scoop: 0.45 };
const KAWALA = { tri: 0.18, h2: 0.08, breath: 1.8, onset: 3.6, bp: 2.4, atk: 0.045, lp: 4000, vib: [5.3, 10], scoop: 0.35 };

/**
 * createNey(ac, out, send, plan, opts) → { note, dispose }   plan.lead.timbre 'ney' | 'kawala' (the Egyptian end-blown
 * flute: brighter, quicker); plan.oud: { maqam, scale, map, orn }; plan.key.tonic. opts: { registry, tag, stepDur, r, t0 }.
 * The engine's melody is the key's pentatonic: each note moves by plan.oud.map[degree] into the maqam (Hijaz: 1 ♭2 3 5 ♭6,
 * the augmented second between ♭2 and 3; Kurd: 1 ♭3 4 5 ♭6; Ajam and Nahawand as written).
 * Tone: sine f + triangle f (.1 ney / .18 kawala) + sine 2f (.05 / .08). Breath: white noise → BP 1.9·f (Q .7, kawala 2.4·f,
 * 900–4200 Hz) + HP 5.2 kHz (.35) inside its own envelope: a puff (2.2 × the tone's level) as the air starts, then .9 of it
 * (≈ 18 dB under the tone); the tone speaks 15 ms after the air with τ 65 ms (45). Notes over 0.3 s scoop up from 40–90 ¢
 * below (p .45 / .35, τ 70 ms); slurred notes glide (τ 45 ms), a repeated note is breathed again. Vibrato 4.6 Hz ±13 ¢ (5.3 Hz ±10) with ±5 % of breath,
 * deepening from 0.35 s (τ .45 s), only on notes over 0.5 s. Release τ 90 ms.
 * Ornaments in the maqam, before notes of 0.22 s or more (p plan.oud.orn): a turn (the maqam's note above, the note, the one
 * below; 40 %, notes ≥ 0.4 s), a trill with the note above (25 %, notes ≥ 0.55 s), else an appoggiatura from above; each
 * grace 45–65 ms. Nahawand's note below the tonic is the leading tone.
 * → LP 2.6k (3.2k) → pan lead.pan; a quarter-note echo (fb .28, LP 1.8k, wet .2), reverb send .55.
 */
export function createNey(ac, out, send, plan, { registry, tag, stepDur, r, t0 = ac.currentTime }) {
  const lead = plan.lead, P = plan.oud, T = lead.timbre === 'kawala' ? KAWALA : NEY, tonic = plan.key.tonic;
  const inScale = m => P.scale.includes(mod12(m - tonic));
  const nb = (m, dir) => {
    if (dir < 0 && P.maqam === 'nahawand' && mod12(m - tonic) === 0) return m - 1;
    for (let k = m + dir; Math.abs(k - m) <= 3; k += dir) if (inScale(k)) return k;
    return null;
  };
  const nodes = [], N = x => (nodes.push(x), x);
  const o1 = N(osc(ac, 'sine', 440)), o2 = N(osc(ac, 'triangle', 440)), g2 = N(gain(ac, T.tri)), o3 = N(osc(ac, 'sine', 880)), g3 = N(gain(ac, T.h2));
  const tone = N(gain(ac, 0));
  o1.connect(tone); o2.connect(g2); g2.connect(tone); o3.connect(g3); g3.connect(tone);
  const src = N(ac.createBufferSource());
  src.buffer = noise(ac, 'white'); src.loop = true;
  const bp = N(filter(ac, 'bandpass', 1800, 0.7)), hiss = N(filter(ac, 'highpass', 5200, 0.7)), hissG = N(gain(ac, 0.35)), breath = N(gain(ac, 0));
  src.connect(bp); bp.connect(breath); src.connect(hiss); hiss.connect(hissG); hissG.connect(breath);
  const lp = N(filter(ac, 'lowpass', T.lp, 0.5)), trem = N(gain(ac, 1)), pan = N(panner(ac, lead.pan || 0)), sendG = N(gain(ac, 0.55));
  tone.connect(lp); breath.connect(lp); lp.connect(trem); trem.connect(pan);
  const dly = N(ac.createDelay(2)), dLP = N(filter(ac, 'lowpass', 1800, 0.5)), fb = N(gain(ac, 0.28)), wet = N(gain(ac, 0.2));
  dly.delayTime.value = Math.min(1.9, 4 * stepDur);
  trem.connect(dly); dly.connect(dLP); dLP.connect(fb); fb.connect(dly); dLP.connect(wet); wet.connect(pan);
  pan.connect(out); pan.connect(sendG); sendG.connect(send);
  const vib = N(osc(ac, 'sine', T.vib[0])), vibG = N(gain(ac, 0)), tremG = N(gain(ac, 0));
  vib.connect(vibG); for (const o of [o1, o2, o3]) vibG.connect(o.detune); vib.connect(tremG); tremG.connect(trem.gain);
  const p = persistent(registry, tag, [o1, o2, o3, src, vib], nodes, t0, 1.9);
  const setF = (hz, at, tau) => {
    for (const [o, k] of [[o1, 1], [o2, 1], [o3, 2]]) { if (tau) o.frequency.setTargetAtTime(k * hz, at, tau); else o.frequency.setValueAtTime(k * hz, at); }
  };
  /** the grace notes before a note of d s: [[midi, s]…] or null */
  function ornament(m, d) {
    if (d < 0.22 || R() >= P.orn) return null;
    const up = nb(m, 1), dn = nb(m, -1), g = clamp(0.3 * stepDur, 0.045, 0.065), u = R();
    if (u < 0.4 && up != null && dn != null && d >= 0.4) return [[up, g], [m, g], [dn, g]];
    if (u < 0.65 && up != null && d >= 0.55) return [[up, g], [m, g], [up, g], [m, g], [up, g]];
    return up != null ? [[up, 1.3 * g]] : null;
  }
  const base = Math.floor(r() * 4294967296);
  let off = -1, last = null, R = r;
  return {
    note(t, midi, vel, dur, glide = false) {
      const m = midi + (P.map[mod12(midi - tonic)] || 0), d = Math.max(0.08, dur), f = mtof(m), A = 0.2 * vel * BAND.lead;
      R = rng(seedOf(base, midi, vel, dur));                                  // this note's own ornament draws
      if (m === last) glide = false;                                          // a repeated note is breathed again
      last = m;
      for (const prm of [o1.frequency, o2.frequency, o3.frequency, tone.gain, breath.gain, vibG.gain, tremG.gain]) hold(prm, t);
      const orn = ornament(m, d);
      if (orn) {
        let tk = t;
        orn.forEach(([x, gd], k) => { setF(mtof(x), tk, glide || k ? 0.006 : 0); tk += gd; });
        setF(f, tk, 0.007);
      } else if (glide) setF(f, t, 0.045);
      else if (d > 0.3 && R() < T.scoop) { setF(f * Math.pow(2, -(40 + 50 * R()) / 1200), t, 0); setF(f, t + 0.02, 0.07); }
      else setF(f, t, 0);
      bp.frequency.setValueAtTime(clamp(T.bp * f, 900, 4200), t);
      if (glide) { tone.gain.setTargetAtTime(A, t, 0.05); breath.gain.setTargetAtTime(1.3 * T.breath * A, t, 0.02); }
      else {
        if (t < off) tone.gain.setTargetAtTime(0.35 * A, t, 0.01);           // the breath breaks between notes
        tone.gain.setTargetAtTime(A, t + 0.015, T.atk);
        breath.gain.setTargetAtTime(T.onset * A, t, 0.008);
      }
      breath.gain.setTargetAtTime(T.breath * A, t + 0.05, 0.09);
      tone.gain.setTargetAtTime(0, t + d, 0.09);
      breath.gain.setTargetAtTime(0, t + d, 0.07);
      vibG.gain.setTargetAtTime(0, t, 0.05); tremG.gain.setTargetAtTime(0, t, 0.05);
      if (d > 0.5) { vibG.gain.setTargetAtTime(T.vib[1], t + 0.35, 0.45); tremG.gain.setTargetAtTime(0.05, t + 0.35, 0.45); }
      off = t + d;
    },
    dispose(t) { p.dispose(t); },
  };
}

/* ---------------- darbuka + riq (one noise source) ---------------- */
/** kit: { doumF0, doumF1, doumDecay, tekBP, tekPing, tekDecay, jingle, lp }; opts: { registry, tag, t0, stepDur }
 *  kick = doum, the open bass stroke: sine doumF0 (96–112) → doumF1 (64–74) Hz (τ 30 ms), .5v, attack τ 1.5 ms, decay τ
 *         doumDecay (.2–.3 s: the goblet rings) + the skin's 2nd mode (sine 2.3 f, .16v, τ 45 ms) + the palm (noise → BP
 *         520 Hz, Q 1, .1v, τ 7 ms)
 *  snare = tek, the rim stroke: noise → HP 1.5k → BP tekBP (2.9–3.9 kHz, Q 1.1), .6v, τ tekDecay (16–26 ms; ghost 9 ms) +
 *         the ring (sine tekPing 620–780 Hz from 4 % sharp, τ 15 ms, .24v, τ 40 ms, and 2.7× it, .085v, τ 22 ms), attack
 *         τ .7 ms, pan +.08, reverb .18
 *  rim = ka, the other hand's tek: noise → HP 1.5k → BP .72·tekBP (Q 1.3), .3v, τ 12 ms + the ring .08v (attack τ .7 ms);
 *         a ka within 1.2 steps of the last one is a roll: two strokes a 32nd apart (the second .7), pan −.12, reverb .15
 *  hat = riq jingles: noise → BP 6.8 kHz (Q 2.6) + BP 9.6 kHz (Q 3.2), .13v, two clashes 0 / 11 ms (1, .5), τ jingle
 *         (40–65 ms); open = a shake, 6 clashes over 120 ms, τ .2 s; pan +.28, reverb .12
 *  shaker = the riq rolled softly: 3 clashes over 45 ms, .07v, τ 60 ms
 *  tom = the doholla (a bass darbuka): sine 118 → 80 Hz (τ 50 ms), .36v, τ .26 s, pan −.1, reverb .15 */
export function createDarbuka(ac, out, send, kit, { registry, tag, t0 = ac.currentTime, stepDur = 0.2 }) {
  const L = LEVELS.kit * BAND.kit, nodes = [], N = x => (nodes.push(x), x);
  const src = N(ac.createBufferSource());
  src.buffer = noise(ac, 'white'); src.loop = true;
  const bus = N(filter(ac, 'lowpass', kit.lp, 0.5));
  bus.connect(out);
  const dOsc = N(osc(ac, 'sine', kit.doumF1)), dAmp = N(gain(ac, 0)), dOsc2 = N(osc(ac, 'sine', 2.3 * kit.doumF1)), dAmp2 = N(gain(ac, 0));
  const dBP = N(filter(ac, 'bandpass', 520, 1)), dPalm = N(gain(ac, 0));
  dOsc.connect(dAmp); dAmp.connect(bus); dOsc2.connect(dAmp2); dAmp2.connect(bus); src.connect(dBP); dBP.connect(dPalm); dPalm.connect(bus);
  const tHP = N(filter(ac, 'highpass', 1500, 0.7)), tBP = N(filter(ac, 'bandpass', kit.tekBP, 1.1)), tAmp = N(gain(ac, 0));
  const tPing = N(osc(ac, 'sine', kit.tekPing)), tPingG = N(gain(ac, 0)), tRing = N(osc(ac, 'sine', 2.7 * kit.tekPing)), tRingG = N(gain(ac, 0));
  const tPan = N(panner(ac, 0.08)), tSend = N(gain(ac, 0.18));
  src.connect(tHP); tHP.connect(tBP); tBP.connect(tAmp); tAmp.connect(tPan);
  tPing.connect(tPingG); tPingG.connect(tPan); tRing.connect(tRingG); tRingG.connect(tPan);
  tPan.connect(bus); tPan.connect(tSend); tSend.connect(send);
  const kBP = N(filter(ac, 'bandpass', 0.72 * kit.tekBP, 1.3)), kAmp = N(gain(ac, 0)), kPingG = N(gain(ac, 0)), kPan = N(panner(ac, -0.12)), kSend = N(gain(ac, 0.15));
  tHP.connect(kBP); kBP.connect(kAmp); kAmp.connect(kPan); tPing.connect(kPingG); kPingG.connect(kPan);
  kPan.connect(bus); kPan.connect(kSend); kSend.connect(send);
  const j1 = N(filter(ac, 'bandpass', 6800, 2.6)), j2 = N(filter(ac, 'bandpass', 9600, 3.2)), jAmp = N(gain(ac, 0)), jPan = N(panner(ac, 0.28)), jSend = N(gain(ac, 0.12));
  src.connect(j1); src.connect(j2); j1.connect(jAmp); j2.connect(jAmp); jAmp.connect(jPan); jPan.connect(bus); jPan.connect(jSend); jSend.connect(send);
  const hOsc = N(osc(ac, 'sine', 80)), hAmp = N(gain(ac, 0)), hPan = N(panner(ac, -0.1)), hSend = N(gain(ac, 0.15));
  hOsc.connect(hAmp); hAmp.connect(hPan); hPan.connect(bus); hPan.connect(hSend); hSend.connect(send);
  const p = persistent(registry, tag, [src, dOsc, dOsc2, tPing, tRing, hOsc], nodes, t0, 0.4);
  const hit = (param, peak, t, atk, rel, tau) => { param.setTargetAtTime(peak, t, atk); param.setTargetAtTime(0, t + rel, tau); };
  const jingle = (t, A, bursts, tau) => bursts.forEach(([d, w], k) => hit(jAmp.gain, A * w, t + d, 0.0006, 0.002, k === bursts.length - 1 ? tau : 0.012));
  const ka = (t, v) => { hit(kAmp.gain, 0.3 * v * L, t, 0.0007, 0.002, 0.012); hit(kPingG.gain, 0.08 * v * L, t, 0.0007, 0.002, 0.03); };
  let lastKa = -Infinity;
  return {
    kick(t, v) {
      dOsc.frequency.setValueAtTime(kit.doumF0, t); dOsc.frequency.setTargetAtTime(kit.doumF1, t, 0.03);
      dOsc2.frequency.setValueAtTime(2.3 * kit.doumF0, t); dOsc2.frequency.setTargetAtTime(2.3 * kit.doumF1, t, 0.03);
      hit(dAmp.gain, 0.5 * v * L, t, 0.0015, 0.008, kit.doumDecay);
      hit(dAmp2.gain, 0.16 * v * L, t, 0.0012, 0.004, 0.045);
      hit(dPalm.gain, 0.1 * v * L, t, 0.001, 0.003, 0.007);
    },
    snare(t, v, ghost = false) {
      tPing.frequency.setValueAtTime(1.04 * kit.tekPing, t); tPing.frequency.setTargetAtTime(kit.tekPing, t, 0.015);
      hit(tAmp.gain, 0.6 * v * L, t, 0.0007, 0.002, ghost ? 0.009 : kit.tekDecay);
      hit(tPingG.gain, (ghost ? 0.1 : 0.24) * v * L, t, 0.0006, 0.003, 0.04);
      hit(tRingG.gain, (ghost ? 0.035 : 0.085) * v * L, t, 0.0005, 0.002, 0.022);
    },
    rim(t, v) {
      const roll = t > lastKa && t - lastKa < 1.2 * stepDur;
      ka(t, v);
      if (roll) ka(t + stepDur / 2, 0.7 * v);
      lastKa = t;
    },
    hat(t, v, open = false) {
      if (open) jingle(t, 0.13 * v * L, [[0, 1], [0.02, 0.7], [0.045, 0.85], [0.07, 0.6], [0.095, 0.55], [0.12, 0.45]], 0.2);
      else jingle(t, 0.13 * v * L, [[0, 1], [0.011, 0.5]], kit.jingle);
    },
    shaker(t, v) { jingle(t, 0.07 * v * L, [[0, 0.7], [0.022, 1], [0.045, 0.7]], 0.06); },
    tom(t, v) {
      hOsc.frequency.setValueAtTime(118, t); hOsc.frequency.setTargetAtTime(80, t, 0.05);
      hit(hAmp.gain, 0.36 * v * L, t, 0.0015, 0.006, 0.26);
    },
    dispose(t) { p.dispose(t); },
  };
}
